import Instance from "./Instance";

// Category management (CATEGORY_API.md §4 — dashboard/admin track).
// Reads need any staff JWT; writes need role admin / ipadadmin. The shared
// Instance already attaches Authorization: Bearer <token>.
//
// Success envelope: { status: true, data: … }
// Error envelope:   { status: false, msg: "…" }  (and sometimes data, e.g. the
//                    409 on DELETE carries data.productCount).

// §4.1 — list ALL categories (incl. inactive/empty). productCount = every
// product (published or not), which is why it differs from the storefront count.
export const getCategories = async () => {
    try {
        const response = await Instance.get("/api/v1/categories");
        return response.data;
    } catch (error) {
        console.error("Error fetching categories:", error);
        throw error;
    }
};

// §4.2 — create. body: { name (required, 1–80), sortOrder?, isActive?, image? }.
// 409 "That category already exists." when the name collides (names are
// case/spacing-insensitive).
export const createCategory = async (body) => {
    try {
        const response = await Instance.post("/api/v1/categories", body);
        return response.data;
    } catch (error) {
        console.error("Error creating category:", error);
        throw error;
    }
};

// §4.3 — edit. Send only changed fields: { name?, sortOrder?, isActive?, image? }.
// Renaming is instant everywhere (products reference by id — no cascade).
export const updateCategory = async (id, body) => {
    try {
        const response = await Instance.put(`/api/v1/categories/${id}`, body);
        return response.data;
    } catch (error) {
        console.error("Error updating category:", error);
        throw error;
    }
};

// §4.4 — delete. Refuses with 409 { msg, data.productCount } while products
// still use it; pass force=true to un-categorize those products, then delete.
export const deleteCategory = async (id, force = false) => {
    try {
        const response = await Instance.delete(`/api/v1/categories/${id}`, {
            params: force ? { force: true } : {}
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting category:", error);
        throw error;
    }
};

// §4.4b — delete EVERY category in one call. Guarded: must pass confirm=true,
// else the backend refuses with 400. On success it un-categorizes every product
// first (category: null, categoryLocked: false), then deletes all categories.
// Returns data.categoriesDeleted / data.productsUnassigned.
export const deleteAllCategories = async (confirm = false) => {
    try {
        const response = await Instance.delete("/api/v1/categories", {
            params: confirm ? { confirm: true } : {}
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting all categories:", error);
        throw error;
    }
};

// §4.5 — move a PRODUCT to a category (or clear it). Pass category: null to
// clear + unlock. Any non-null value sets categoryLocked: true, pinning the
// product so the first-word rule won't move it again.
// This is the ONLY way to change a product's category — PUT /updateProduct
// silently drops category/categoryLocked by design.
export const setProductCategory = async (id, category) => {
    try {
        const response = await Instance.patch(`/api/v1/setProductCategory/${id}`, {
            category
        });
        return response.data;
    } catch (error) {
        console.error("Error setting product category:", error);
        throw error;
    }
};

// §4.6 — one-time backfill: seed categories from product names. Idempotent
// (safe to re-run). overwrite=true also re-derives AUTO-assigned products;
// manually locked products are always skipped.
export const backfillCategories = async (overwrite = false) => {
    try {
        const response = await Instance.post(
            "/api/v1/categories/backfill",
            {},
            { params: overwrite ? { overwrite: true } : {} }
        );
        return response.data;
    } catch (error) {
        console.error("Error backfilling categories:", error);
        throw error;
    }
};
